import React, { useState } from 'react';
import { ClipboardCheck, Plus, Loader2 } from 'lucide-react';
import { Card, Btn, LabeledInput, Empty } from './shared';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import BadgesGrid from '../BadgesGrid';
import AchievementsGrid from '../AchievementsGrid';
import MeasurementsCard from '../MeasurementsCard';

const Progress = ({ initialCheckins = [] }) => {
  const [checkins, setCheckins] = useState(initialCheckins);
  const [f, setF] = useState({ weight_kg: 80, adherence: 7, recovery: 6, energy: 7, notes: '' });
  const [busy, setBusy] = useState(false);
  const [badgesKey, setBadgesKey] = useState(0);
  const { toast } = useToast();

  React.useEffect(() => { setCheckins(initialCheckins); }, [initialCheckins]);

  const setNum = (k) => (e) => setF({ ...f, [k]: Number(e.target.value) });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const saved = await api.addCheckin({ ...f });
      setCheckins([...checkins, saved]);
      toast({ title: 'Check-in logged' });
      // Ping streak so check-ins count as activity
      try { await api.streakPing('checkin'); setBadgesKey((k) => k + 1); } catch {}
    } catch (err) {
      toast({ title: 'Could not log', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const sorted = [...checkins].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const weights = sorted.map((c) => c.weight_kg);
  const min = weights.length ? Math.min(...weights) : 0;
  const max = weights.length ? Math.max(...weights) : 0;
  const range = Math.max(max - min, 1);

  return (
    <div className="space-y-6">
      <BadgesGrid refreshKey={badgesKey} />
      <AchievementsGrid refreshKey={badgesKey} />
      <MeasurementsCard />
      <div className="grid lg:grid-cols-2 gap-6">
      <Card title="Weekly check-in" icon={ClipboardCheck}>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <LabeledInput label="Weight (kg)" type="number" step="0.1" value={f.weight_kg} onChange={setNum('weight_kg')} required />
            <LabeledInput label="Adherence (1–10)" type="number" min="1" max="10" value={f.adherence} onChange={setNum('adherence')} />
            <LabeledInput label="Recovery (1–10)" type="number" min="1" max="10" value={f.recovery} onChange={setNum('recovery')} />
            <LabeledInput label="Energy (1–10)" type="number" min="1" max="10" value={f.energy} onChange={setNum('energy')} />
          </div>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-[#cbd5e1] font-medium">Notes (optional)</span>
            <textarea
              rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
              placeholder="sleep, stress, soreness..."
              className="bg-[#0b0c10] border border-[#2a2f3a] focus:border-[#ff7a1a] focus:ring-2 focus:ring-[#ff7a1a]/20 rounded-lg px-3 py-2.5 text-white outline-none"
            />
          </label>
          <Btn type="submit" disabled={busy} className="w-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Log check-in
          </Btn>
        </form>
      </Card>

      <Card title="Progress">
        {sorted.length === 0 ? (
          <Empty>Log a check-in to start tracking weight trend.</Empty>
        ) : (
          <>
            <p className="text-[#aab1bf] text-sm">{sorted.length} check-in{sorted.length === 1 ? '' : 's'}</p>
            <div className="mt-4 flex items-end gap-1.5 h-36">
              {sorted.map((c, i) => {
                const h = 10 + ((c.weight_kg - min) / range) * 110;
                return (
                  <div key={c.id} className="flex-1 flex flex-col items-center gap-1" title={`${c.weight_kg}kg`}>
                    <div className="w-full bg-gradient-to-t from-[#ff7a1a] to-[#ffa05a] rounded-t" style={{ height: `${h}px` }} />
                    <span className="text-[10px] text-[#7c8597]">{i + 1}</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex justify-between text-xs text-[#7c8597]">
              <span>min {min}kg</span>
              <span>latest {sorted[sorted.length - 1]?.weight_kg}kg</span>
              <span>max {max}kg</span>
            </div>
          </>
        )}
      </Card>
      </div>
    </div>
  );
};

export default Progress;
